// Paid-attribution capture (Google Ads / Meta Ads + UTMs) — WEB side.
//
// SEPARATION OF CONCERNS (per PAID ATTRIBUTION phase spec):
//   A. Technical capture capability (this module): read identifiable paid/campaign
//      signals from the entry URL and transport them to the CRM with the lead.
//   B. Persistence policy per consent (documented below): what we keep and for how long.
//
// Persistence mechanism: first-party localStorage key `de_attribution_v1`. This is
// FUNCTIONAL/service storage used solely to attribute a visitor's OWN form submission
// to its traffic origin. It is NOT advertising storage: it sets no third-party/ad
// cookies, never flips Google Consent Mode, and leaves ad_storage/analytics_storage
// governed exclusively by lib/consent.js (still denied by default).
//
// We NEVER mark marketing/ad consent as granted merely because a UTM/GCLID/FBCLID
// exists, and we NEVER fabricate a Google/Meta touch for traffic without identifiable
// parameters (direct/organic/internal → no touch recorded).
//
// POLICY_PENDING (legal, does NOT block this technical capability or DEV tests):
//   long-term retention / lawful basis for advertising click identifiers
//   (gclid/gbraid/wbraid/fbclid) pending the advertising-consent review.
//
// CONSENT GATE (preproduction): paid attribution is persisted to localStorage and
// transported to the CRM ONLY when advertising/ad-storage consent is "granted".
// The signal is derived from the SINGLE existing consent store (./consent) — no
// second consent system:
//   getConsent()===null → "unknown"   analytics:false → "denied"   analytics:true → "granted".
// While unknown/denied: nothing is written to localStorage and getAttribution() returns
// null (no first/last, no click_ids to the CRM, no fabrication). On withdrawal the stored
// paid attribution is deleted. The rest of the site and the forms keep working normally;
// the commercial/marketing consent is not modified here.

import { getConsent, onConsentChange } from "./consent";

const KEY = "de_attribution_v1";
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90-day attribution window (functional)

const UTM_KEYS = ["source", "medium", "campaign", "content", "term"];
const CLICK_KEYS = ["gclid", "gbraid", "wbraid", "fbclid"];

// Advertising/ad-storage consent state derived from the one existing consent store.
export function adConsentState() {
  const c = getConsent();
  if (!c) return "unknown";
  return c.analytics ? "granted" : "denied";
}
const adGranted = () => adConsentState() === "granted";

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (!v) return { first: null, last: null };
    return { first: v.first || null, last: v.last || null };
  } catch {
    return { first: null, last: null };
  }
}

function write(first, last) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ first: first || null, last: last || null, at: new Date().toISOString() }));
  } catch {
    /* storage unavailable — capture degrades silently */
  }
}

function clip(v, max) {
  if (v == null) return undefined;
  const s = String(v).trim();
  return s ? s.slice(0, max) : undefined;
}

// Build a canonical touch from the current location/referrer.
// Returns null when the visit carries NO identifiable paid/campaign signal
// (no utm_* and no click id) — a direct/organic/internal visit is never a touch.
export function buildTouchFromLocation(loc = window.location, referrer = (typeof document !== "undefined" ? document.referrer : "")) {
  const params = new URLSearchParams(loc.search || "");
  const utm = {};
  UTM_KEYS.forEach((k) => { const v = clip(params.get(`utm_${k}`), 300); if (v) utm[k] = v; });
  const click = {};
  CLICK_KEYS.forEach((k) => { const v = clip(params.get(k), 512); if (v) click[k] = v; });
  if (!Object.keys(utm).length && !Object.keys(click).length) return null;
  const touch = { occurred_at: new Date().toISOString() };
  if (Object.keys(utm).length) touch.utm = utm;
  if (Object.keys(click).length) touch.click_ids = click;
  const landing = clip(loc.href, 2048); if (landing) touch.landing_url = landing;
  const path = clip((loc.pathname || "") + (loc.search || ""), 2048); if (path) touch.page_path = path;
  const ref = clip(referrer, 2048); if (ref) touch.referrer = ref;
  return touch;
}

// Pure first/last rule: FIRST is write-once (never overwritten), LAST advances to the
// newest identifiable touch. A null touch (direct/internal navigation) changes nothing.
export function applyTouch(store, touch) {
  const first = (store && store.first) || null;
  const last = (store && store.last) || null;
  if (!touch) return { first, last };
  return { first: first || touch, last: touch };
}

// Capture on page load / SPA route change. Safe to call on every navigation.
export function captureAttribution() {
  if (!adGranted()) return read(); // unknown/denied → never persist paid attribution
  let touch = null;
  try { touch = buildTouchFromLocation(); } catch { touch = null; }
  if (!touch) return read();
  const next = applyTouch(read(), touch);
  write(next.first, next.last);
  return next;
}

// Attribution object to attach to a form submit: { first?, last? } or null when nothing captured.
export function getAttribution() {
  if (!adGranted()) return null; // unknown/denied → nothing travels to the CRM
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? JSON.parse(raw) : null;
    if (v?.at && Date.now() - Date.parse(v.at) > MAX_AGE_MS) { localStorage.removeItem(KEY); return null; }
  } catch {
    /* ignore */
  }
  const { first, last } = read();
  if (!first && !last) return null;
  const out = {};
  if (first) out.first = first;
  if (last) out.last = last;
  return out;
}

// Wire the consent gate: capture now if already granted; on grant-after-landing capture
// ONLY the params still present in the current URL (never reconstruct a past touch); on
// denial/withdrawal delete any stored paid attribution. Returns an unsubscribe fn.
export function initAttributionConsentGate() {
  captureAttribution();
  return onConsentChange(() => {
    if (adGranted()) captureAttribution();
    else _clearAttribution();
  });
}

// test/util
export function _clearAttribution() { try { localStorage.removeItem(KEY); } catch { /* ignore */ } }
